"use client";

import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { Menu, X, Phone, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCartLineCount } from "@/hooks/useCart";

// 뱃지에 표시할 최대 숫자. 이보다 많으면 "99+"로 줄여 뱃지가 커지는 것을 막는다.
const CART_BADGE_MAX_COUNT = 99;

const NAV_LINKS = [
  { href: "/", label: "홈" },
  { href: "/products", label: "상품" },
  { href: "/farm-diary", label: "농장일지" },
  { href: "/about", label: "농장소개" },
  { href: "/notice", label: "공지사항" },
  { href: "/contact", label: "고객센터" },
  { href: "/order/lookup", label: "주문조회" },
];

interface HeaderProps {
  phone?: string;
}

export function Header({ phone }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const cartLineCount = useCartLineCount();
  const cartBadgeLabel =
    cartLineCount > CART_BADGE_MAX_COUNT
      ? `${CART_BADGE_MAX_COUNT}+`
      : String(cartLineCount);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Focus first link when drawer opens
  useEffect(() => {
    if (isMenuOpen) {
      firstLinkRef.current?.focus();
    }
  }, [isMenuOpen]);

  // Escape key closes drawer
  useEffect(() => {
    if (!isMenuOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  // Focus trap inside drawer
  function handleDrawerKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const focusable = e.currentTarget.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function closeMenu() {
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  }

  function isActive(href: string) {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  }

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 bg-[var(--color-primary)] transition-shadow duration-200",
          "border-b-2 border-[var(--color-gold)]/30",
          isScrolled && "shadow-lg",
        )}
      >
        <div className="container">
          <div className="flex items-center justify-between h-[68px]">
            {/* 로고 */}
            <Link
              href="/"
              className="flex items-center gap-3 hover:opacity-90 transition-opacity"
            >
              <div className="leading-tight">
                <span
                  className="block text-[var(--color-gold)] font-bold text-xl"
                  style={{
                    fontFamily: "var(--font-serif)",
                    letterSpacing: "0.15em",
                  }}
                >
                  현 들 팜
                </span>
                <span
                  className="block text-[var(--color-gold)]/60 text-[0.6rem] uppercase"
                  style={{
                    fontFamily: "var(--font-display)",
                    letterSpacing: "0.25em",
                  }}
                >
                  HYUNDEUL FARM
                </span>
              </div>
            </Link>

            {/* 데스크탑 네비게이션 */}
            <nav
              className="hidden md:flex items-center gap-1"
              aria-label="주 메뉴"
            >
              {NAV_LINKS.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "px-4 py-2 rounded-lg text-[0.95rem] transition-all duration-200 transform-gpu hover:scale-105",
                      active
                        ? "text-[var(--color-gold)] font-semibold"
                        : "text-white/85 font-medium hover:text-[var(--color-gold)]",
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {/* 우측: 장바구니 + 전화번호 + 햄버거 */}
            <div className="flex items-center gap-3">
              <Link
                href="/cart"
                className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors text-white"
                aria-label={
                  cartLineCount > 0
                    ? `장바구니 (담긴 품목 ${cartLineCount}개)`
                    : "장바구니"
                }
              >
                <ShoppingBag className="h-5 w-5" />
                {cartLineCount > 0 && (
                  <span
                    aria-hidden
                    className="absolute top-0.5 right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full bg-[var(--color-gold)] text-[var(--color-primary-dark)] text-[0.65rem] font-bold leading-none"
                  >
                    {cartBadgeLabel}
                  </span>
                )}
              </Link>

              {phone && (
                <a
                  href={`tel:${phone.replace(/-/g, "")}`}
                  className="hidden sm:flex items-center gap-1.5 text-[var(--color-gold)] font-semibold text-sm hover:opacity-80 transition-opacity"
                >
                  <Phone className="h-4 w-4" />
                  <span>{phone}</span>
                </a>
              )}

              <button
                ref={menuButtonRef}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors text-white"
                onClick={() => setIsMenuOpen(true)}
                aria-label="메뉴 열기"
                aria-expanded={isMenuOpen}
                aria-haspopup="dialog"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 모바일 드로어 */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* 백드롭 */}
          <button
            className="absolute inset-0 bg-black/40 w-full h-full cursor-default"
            onClick={closeMenu}
            aria-label="메뉴 닫기"
            tabIndex={-1}
          />
          {/* 드로어 패널 */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-title"
            className="absolute right-0 top-0 h-full w-72 shadow-2xl flex flex-col bg-[var(--color-primary)]"
            onKeyDown={handleDrawerKeyDown}
          >
            <h2 id="drawer-title" className="sr-only">
              메뉴
            </h2>
            <div className="flex items-center justify-between px-5 h-[68px] border-b border-[var(--color-gold)]/30">
              <span
                className="text-[var(--color-gold)] font-bold text-xl"
                style={{
                  fontFamily: "var(--font-serif)",
                  letterSpacing: "0.15em",
                }}
              >
                현 들 팜
              </span>
              <button
                className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors text-white"
                onClick={closeMenu}
                aria-label="메뉴 닫기"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav
              className="flex-1 px-4 py-6 flex flex-col gap-1"
              aria-label="모바일 메뉴"
            >
              <Link
                href="/cart"
                ref={firstLinkRef}
                aria-current={isActive("/cart") ? "page" : undefined}
                className={cn(
                  "flex items-center justify-between px-4 py-3 font-medium rounded-lg transition-colors text-base min-h-[48px]",
                  isActive("/cart")
                    ? "bg-white/15 text-[var(--color-gold)]"
                    : "text-white/85 hover:bg-white/10 hover:text-[var(--color-gold)]",
                )}
                onClick={closeMenu}
              >
                <span>장바구니</span>
                {cartLineCount > 0 && (
                  <span className="min-w-[1.4rem] h-[1.4rem] px-1.5 flex items-center justify-center rounded-full bg-[var(--color-gold)] text-[var(--color-primary-dark)] text-xs font-bold leading-none">
                    {cartBadgeLabel}
                  </span>
                )}
              </Link>
              {NAV_LINKS.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center px-4 py-3 font-medium rounded-lg transition-colors text-base min-h-[48px]",
                      active
                        ? "bg-white/15 text-[var(--color-gold)]"
                        : "text-white/85 hover:bg-white/10 hover:text-[var(--color-gold)]",
                    )}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            {phone && (
              <div className="px-5 py-6 border-t border-[var(--color-gold)]/20">
                <a
                  href={`tel:${phone.replace(/-/g, "")}`}
                  className="flex items-center gap-2 text-[var(--color-gold)] font-semibold"
                >
                  <Phone className="h-4 w-4" />
                  <span>{phone}</span>
                </a>
                <p className="mt-1 text-xs text-white/50">평일 09:00 - 18:00</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
